import { useQuery, type QueryClient } from "@tanstack/react-query";
import type { AppConfig, AuditDetailDto, AuditDto, Page, QuestionDto, SupplierDto } from "@audit-plan/shared";
import { api } from "./client";

/** Le liste possono arrivare come array o come { items, total }: normalizzo sempre ad array. */
export const unwrap = <T,>(d: T[] | Page<T> | undefined | null): T[] => (!d ? [] : Array.isArray(d) ? d : (d.items ?? []));

export type AuditFilters = {
  q?: string;
  status?: string;
  type?: string;
  supplierId?: string;
  auditorId?: string;
  year?: number;
  origin?: string;
  client?: string;
};

/** Clienti noti per l'audit cliente: elenco fisso + quelli già usati negli audit. */
export const KNOWN_CLIENTS = ["Loewe", "Kering", "Chanel", "LV", "Bulgari", "Celine", "Dior", "Loro Piana"];
export function useAuditClients() {
  const all = useAudits({});
  const used = (all.data ?? []).map((a) => a.client).filter(Boolean);
  const set = new Map<string, string>();
  for (const c of [...KNOWN_CLIENTS, ...used]) if (!set.has(c.toLowerCase())) set.set(c.toLowerCase(), c);
  return [...set.values()].sort((a, b) => a.localeCompare(b));
}

export function useAudits(f: AuditFilters = {}, enabled = true) {
  return useQuery({ queryKey: ["audits", "b", f], queryFn: async () => unwrap(await api.get<AuditDto[] | Page<AuditDto>>("/audits", f)), enabled });
}

export function useAudit(id?: string | null) {
  return useQuery({ queryKey: ["audit", id], queryFn: () => api.get<AuditDetailDto>(`/audits/${id}`), enabled: !!id });
}

export function useQuestions(versionId?: string | null) {
  return useQuery({
    queryKey: ["questions", versionId],
    queryFn: async () => unwrap(await api.get<QuestionDto[] | Page<QuestionDto>>(`/checklists/${versionId}/questions`)),
    enabled: !!versionId,
    staleTime: 5 * 60_000,
  });
}

export function useSuppliersList() {
  return useQuery({
    queryKey: ["suppliers", "b-list"],
    // includeInactive vale solo per l'admin (RAT26-F3.1b): può pianificare un audit su un fornitore disattivato
    queryFn: async () => unwrap(await api.get<SupplierDto[] | Page<SupplierDto>>("/suppliers", { includeInactive: true })),
    staleTime: 60_000,
  });
}

export function useConfig() {
  return useQuery({ queryKey: ["config"], queryFn: () => api.get<AppConfig>("/config"), staleTime: 5 * 60_000, retry: false });
}

/** Dopo ogni mutazione: audit, liste, NC, azioni, evidenze e le viste collegate. */
export function invalidateAudit(qc: QueryClient, auditId?: string | null) {
  if (auditId) void qc.invalidateQueries({ queryKey: ["audit", auditId] });
  for (const k of ["audit", "audits", "ncs", "nc", "actions", "action", "evidence", "supplier", "suppliers", "dashboard", "plan"]) {
    void qc.invalidateQueries({ queryKey: [k] });
  }
}
